import React, { useState } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import MediaLibraryModal from "./MediaLibraryModal";
import { API_URL } from "../../../config/axiosApi/axiosApi";

const ImageUpload = ({ 
  imageUrl, 
  setImage,  
  className = "",
  marginLeft = "40px", 
  height = "180px",
  width = "180px",
  buttonText = "Change image",
  uploadText = "Upload Image",
  onRemove,
  centerButton = false,
  useGrid = true,
  showChangeButton = true,
  showRemoveButton = true,
  compactButton = false,
  enableCrop = true,
  cropAspect = 1,
  cropModalTitle = "Crop image",
  mediaLibraryTitle = "Images",
  mediaLibrarySubtitle,
  /** When true, preview fills parent (use inside a sized flex container); centers upload placeholder and image. */
  expandPreview = false,
  /** When true (and expandPreview is false), omit bottom margin on the preview box for flex centering. */
  noPreviewMargin = false,
  /** Used when expandPreview is true — <img> object-fit (default contain). */
  expandObjectFit = "contain",
  /** When true (non-expandPreview), cap preview size to parent (maxWidth/maxHeight 100%). */
  clampPreviewToParent = false,
  /** Optional ref; assigned a function that opens the media library. */
  openLibraryRef = null,
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

  const resolveDisplaySrc = (url) => {
    if (!url) return "";
    if (
      url.startsWith("data:") ||
      url.startsWith("http://") ||
      url.startsWith("https://")
    )
      return url;
    const base = (API_URL || "").replace(/\/$/, "");
    const path = String(url).replace(/^\//, "");
    return `${base}/${path}`;
  };

  const openMediaLibrary = () => setMediaLibraryOpen(true);

  React.useEffect(() => {
    if (!openLibraryRef) return undefined;
    openLibraryRef.current = () => setMediaLibraryOpen(true);
    return () => {
      openLibraryRef.current = null;
    };
  }, [openLibraryRef]);

  const handleRemoveImage = () => {
    setImage("", null);
    if (onRemove) onRemove();
  };

  const handleLibrarySelect = (filePath, altText) => {
    const trimmed = (altText || "").trim();
    setImage(filePath, null, trimmed ? { altText: trimmed.slice(0, 500) } : undefined);
  };

  const containerClass = useGrid
    ? `col-md-3 text-center ${className}`
    : expandPreview
      ? `text-center ${className} d-flex align-items-center justify-content-center w-100 h-100 min-h-0 align-self-stretch`
      : `text-center ${className}`;
  const buttonDivClass = `d-flex ${centerButton ? 'justify-content-center' : ''}`;
  const buttonStyle = { marginLeft: centerButton ? "0" : marginLeft };

  const previewBoxStyle = expandPreview
    ? {
        width: "100%",
        height: "100%",
        minHeight: 0,
        maxHeight: "100%",
        border: "none",
        borderRadius: "12px",
        backgroundColor: "transparent",
      }
    : {
        width: width,
        height: height,
        maxWidth: clampPreviewToParent ? "100%" : undefined,
        maxHeight: clampPreviewToParent ? "100%" : undefined,
        border: imageUrl ? "none" : isDarkMode ? "2px dashed #555" : "2px dashed #D9D9D9",
        borderRadius: "20px",
        backgroundColor: isDarkMode ? "#404040" : "#ffffff",
      };

  const defaultSubtitle =
    "Upload to your library, then click an image to crop and select it for the product.";

  return (
    <>
      <MediaLibraryModal
        isOpen={mediaLibraryOpen}
        onClose={() => setMediaLibraryOpen(false)}
        onSelect={handleLibrarySelect}
        title={mediaLibraryTitle}
        subtitle={mediaLibrarySubtitle || defaultSubtitle}
        enableCrop={enableCrop}
        cropAspect={cropAspect}
        cropModalTitle={cropModalTitle}
      />
      <div className={containerClass}>
        <div
          className={`overflow-hidden position-relative mx-auto ${expandPreview ? "flex-grow-1 align-self-stretch w-100 h-100 min-h-0" : noPreviewMargin ? "mb-0" : "mb-3"}`}
          style={previewBoxStyle}
        >
          {imageUrl ? (
            <>
              <img
                src={resolveDisplaySrc(imageUrl)}
                className="img-fluid"
                alt="Item preview"
                style={{
                  objectFit: expandPreview ? expandObjectFit : "cover",
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                  borderRadius: "20px",
                }}
                onClick={openMediaLibrary}
              />
              {showRemoveButton ? (
              <i
                className="fa fa-times text-danger position-absolute"
                style={{
                  top: "10px",
                  right: "10px",
                  cursor: "pointer",
                  fontSize: "1rem",
                  background: "rgba(255, 255, 255, 0.8)",
                  padding: "3px",
                  borderRadius: "50%",
                }}
                onClick={handleRemoveImage}
              ></i>
              ) : null}
            </>
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center h-100"
              style={{ 
                cursor: "pointer",
                color: isDarkMode ? '#ffffff' : '#6c757d'
              }}
              onClick={openMediaLibrary}
            >
              <i className="fa-solid fa-image" style={{ fontSize: "2.5rem", color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
              <p className="mt-2 mb-0" style={{ color: isDarkMode ? '#ffffff' : '#6c757d' }}>{uploadText}</p>
            </div>
          )}
        </div>
      </div>
      {imageUrl && showChangeButton && (
        <div
          className={buttonDivClass}
          style={{
            ...buttonStyle,
            marginTop: compactButton ? "2px" : "12px",
          }}
        >
          <button
            type="button"
            className={`btn ${compactButton ? "" : "btn-sm rounded-pill"}`}
            onClick={openMediaLibrary}
            style={{
              backgroundColor: compactButton ? "#ffffff" : themeColor,
              borderColor: themeColor,
              color: compactButton ? themeColor : "white",
              padding: compactButton ? "1px 6px" : undefined,
              fontSize: compactButton ? "10px" : undefined,
              lineHeight: compactButton ? "1.2" : undefined,
              borderRadius: compactButton ? "4px" : undefined,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = compactButton
                ? themeColor
                : themeColor + "dd";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = compactButton
                ? "#ffffff"
                : themeColor;
              e.currentTarget.style.color = compactButton ? themeColor : "white";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
           {buttonText}
          </button>
        </div>
      )}
    </>
  );
};

export default ImageUpload;
